import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "internal-platform-systems",
  kind: "horizontal",
  title: "Internal Platform & Workflow Systems",
  shortTitle: "Internal Platforms",
  status: "draft",
  tier: "we-do",
  seoTitle: "Internal Platform & Workflow Systems | GA",
  seoDescription:
    "Bespoke internal platforms, administrative portals, typed APIs, and role-based workflows designed to eliminate operational sprawl and fragmented spreadsheets.",
  summary:
    "Custom internal platforms, administrative portals, typed APIs, and role-based workflows that replace fragile spreadsheet sprawl with dependable operational software.",
  image: "/images/blog/operational-task-management-hero.webp",
  relatedCapabilitySlugs: ["software-architecture-engineering"],
  relatedIndustrySlugs: ["enterprise"],
  relatedPostSlugs: [
    "operational-task-management",
    "dashboard-design",
    "legacy-system-modernization",
  ],
  engineering: [
    "Database-backed administrative portals and bespoke operational web applications",
    "Centralized role-based access control (RBAC), authentication, and permission models",
    "Type-safe REST and GraphQL APIs connecting internal databases and business tools",
    "Operational task management, approval queues, and multi-team handoff workflows",
    "Typed data access layers, schema migrations, and audit-logging infrastructure",
    "Responsive, density-tailored administrative interfaces designed for daily operational use",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Internal Tools Deserve Production Rigor",
    },
    {
      type: "paragraph",
      text: "Organizations often run critical core operations on makeshift spreadsheets, fragile scripts, and disconnected SaaS forms. When internal tools lack clear data models, schema safety, or reliable access controls, daily operations slow down, data leaks between departments, and manual workarounds multiply.",
    },
    {
      type: "heading",
      level: 2,
      text: "Architecture That Matches Operational Reality",
    },
    {
      type: "paragraph",
      text: "Building internal platforms means designing around actual operational handoffs, permission boundaries, and exception states. By establishing typed API contracts, granular role-based permissions, and intuitive administrative interfaces, internal systems give teams tools they can rely on without accumulating quiet technical debt.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map internal business processes, user roles, fragmented data stores, and operational bottlenecks across teams.",
      produces: [
        "Internal workflow, permission, and role-hierarchy analysis",
        "Operational data source and shadow-IT inventory",
        "Process friction, manual workaround, and error-point evaluation",
        "System integration boundary and security constraint specifications",
      ],
      fit: {
        good: [
          "Enterprises seeking to replace brittle spreadsheets, ad-hoc scripts, and legacy internal portals with dependable software",
          "Operations teams needing custom administrative interfaces, structured approval queues, and unified data access",
        ],
        poor: [
          "Teams whose needs are completely satisfied by standard off-the-shelf SaaS ticketing or CRM products",
          "One-off disposable internal scripts where long-term maintainability and access controls are unnecessary",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect the data schema, authentication and RBAC models, API contracts, and primary operational user flows.",
      produces: [
        "Internal platform system architecture and database schema designs",
        "Role-based access control (RBAC) and security boundary specifications",
        "Typed API contracts and external integration architectures",
        "Operational user journeys, navigation structures, and interface wireframes",
      ],
    },
    "detailed-design": {
      focus:
        "Specify component interfaces, validation state machines, database migration strategies, and audit trail schemas.",
      produces: [
        "Administrative UI component contracts and dashboard view specifications",
        "CRUD endpoint, batch processing, and queue handler specifications",
        "Database migration plans and schema validation rules",
        "Audit logging, error tracking, and monitoring specifications",
      ],
    },
    implementation: {
      focus:
        "Build, integrate, and deploy the internal platform, migrating existing operational data and validating workflows with staff.",
      produces: [
        "Production-ready internal platform and administrative applications",
        "Configured role-based access control and secure API integrations",
        "Migrated operational data with validated integrity and audit logging",
        "Administrator documentation, operational runbooks, and staff training guides",
      ],
    },
  },
};
